import React, { useState, useEffect } from 'react';
import {
  Save,
  Download,
  FileCode,
  Undo2,
  Redo2,
  FlaskConical,
  CheckCircle2,
  Clock,
  Settings,
  Info,
  Layers,
  Copy,
  Plus,
  ZoomIn,
  ZoomOut,
  Maximize2,
  FileSpreadsheet,
  Scan,
  Edit3,
} from 'lucide-react';
import { Button } from '../../shared/components/Button';
import { useDocument } from '../../core/state/DocumentContext';
import { SectionItemEditor } from './components/SectionItemEditor';
import { DocumentRenderer } from '../../core/renderer/DocumentRenderer';
import { ManualEditToolbar } from '../../core/manual-edit/ManualEditToolbar';
import { FormulaEditModal } from '../../core/manual-edit/FormulaEditModal';
import { TableEditModal } from '../../core/manual-edit/TableEditModal';
import { useToast } from '../../shared/context/ToastContext';
import { ReportOutlineNav } from './components/ReportOutlineNav';
import { LabTemplateSelectorModal } from './components/LabTemplateSelectorModal';
import { ReportMetadataModal } from './components/ReportMetadataModal';
import { PageSettingsModal } from './components/PageSettingsModal';
import { AddCoverPageModal } from './components/AddCoverPageModal';
import { OCRScannerModal } from './components/OCRScannerModal';
import { LabReportTemplate } from '../../templates/lab-report';
import { CoverTemplate } from '../../templates/types';
import { getDefaultCoverValues } from '../../templates/cover/templateUtils';
import { DocumentModel, DocumentBlock, FormulaBlock, TableBlock } from '../../core/ast/document.types';
import { ExportPreviewModal, ExportSettings } from '../../components/export/ExportPreviewModal';
import { exportDocumentToPdf, downloadPdfBytes } from '../../core/export/pdfExporter';
import { exportDocumentToDocx, downloadDocxBlob } from '../../core/export/docxExporter';
import { validateDocument, sanitizeFilename } from '../../core/export/documentValidator';

export const ReportStudio: React.FC = () => {
  const { showToast } = useToast();
  const {
    document: doc,
    selectedSectionId,
    selectedBlockId,
    setSelectedSectionId,
    setSelectedBlockId,
    saveStatus,
    canUndo,
    canRedo,
    undo,
    redo,
    addSection,
    removeSection,
    updateSection,
    moveSection,
    duplicateSection,
    toggleSectionEnabled,
    addBlock,
    removeBlock,
    updateBlockData,
    duplicateBlock,
    reorderBlockLayer,
    nudgeBlock,
    insertShape,
    updateMetadata,
    updatePageSettings,
    loadDocument,
    manualSave,
    newBlankDocument,
  } = useDocument();

  // Manual Edit Mode state
  const [isManualEditMode, setIsManualEditMode] = useState(false);
  const [editingFormulaBlock, setEditingFormulaBlock] = useState<{ block: FormulaBlock; sectionId: string } | null>(null);
  const [editingTableBlock, setEditingTableBlock] = useState<{ block: TableBlock; sectionId: string } | null>(null);

  // Modals state
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false);
  const [isPageSettingsModalOpen, setIsPageSettingsModalOpen] = useState(false);
  const [isCoverModalOpen, setIsCoverModalOpen] = useState(false);
  const [isOCRModalOpen, setIsOCRModalOpen] = useState(false);
  const [ocrTargetSectionId, setOcrTargetSectionId] = useState<string | null>(null);
  const [isExportPreviewOpen, setIsExportPreviewOpen] = useState(false);
  const [exportPreviewFormat, setExportPreviewFormat] = useState<'pdf' | 'docx'>('pdf');

  // Responsive / Layout View Mode
  const [viewMode, setViewMode] = useState<'split' | 'editor' | 'preview'>('split');
  const [zoomLevel, setZoomLevel] = useState<number>(100);

  // Keyboard shortcut listener (Ctrl+Z, Ctrl+Y, Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        if (canUndo) {
          e.preventDefault();
          undo();
        }
      } else if (
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z')
      ) {
        if (canRedo) {
          e.preventDefault();
          redo();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        manualSave().then(() => showToast('Document saved successfully.', 'success'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, undo, redo, manualSave, showToast]);

  const handleExecutePdfExport = async (exportDoc: DocumentModel, settings: ExportSettings) => {
    const validation = validateDocument(exportDoc);
    if (!validation.isValid) {
      showToast(`Document has errors: ${validation.errors.join(', ')}`, 'error');
      return;
    }
    try {
      const pdfBytes = await exportDocumentToPdf(exportDoc, { isGrayscale: settings.colorMode === 'grayscale' });
      const filename = sanitizeFilename(exportDoc.metadata.title || 'Lab_Report', 'pdf');
      downloadPdfBytes(pdfBytes, filename);
      showToast(`Exported "${filename}" successfully!`, 'success');
    } catch (err) {
      console.error('Failed to export PDF:', err);
      showToast('Error generating PDF document.', 'error');
      throw err;
    }
  };

  const handleExecuteDocxExport = async (exportDoc: DocumentModel, _settings: ExportSettings) => {
    const validation = validateDocument(exportDoc);
    if (!validation.isValid) {
      showToast(`Document has errors: ${validation.errors.join(', ')}`, 'error');
      return;
    }
    try {
      const docxBlob = await exportDocumentToDocx(exportDoc);
      const filename = sanitizeFilename(exportDoc.metadata.title || 'Lab_Report', 'docx');
      downloadDocxBlob(docxBlob, filename);
      showToast(`Exported "${filename}" successfully!`, 'success');
    } catch (err) {
      console.error('Failed to export DOCX:', err);
      showToast('Error generating Word DOCX document.', 'error');
      throw err;
    }
  };

  const handleSelectTemplate = (template: LabReportTemplate | null) => {
    if (!template) {
      newBlankDocument('Untitled Lab Report');
      showToast('Created new blank lab report.', 'success');
    } else {
      const newDoc = template.compileToDocument(template.name, doc.pageSettings);
      loadDocument(newDoc);
      showToast(`Loaded ${template.name}.`, 'success');
    }
  };

  const handleApplyCoverTemplate = (template: CoverTemplate) => {
    const coverVals = getDefaultCoverValues({
      title: doc.metadata.title,
      courseCode: doc.metadata.courseCode || '',
      courseTitle: doc.metadata.courseTitle || '',
      sectionOrGroup: doc.metadata.author.sectionOrGroup || '',
      studentName: doc.metadata.author.studentName || '',
      studentId: doc.metadata.author.studentId || '',
      facultyName: doc.metadata.instructor.instructorName || '',
      facultyDesignation: doc.metadata.instructor.designation || '',
      facultyDepartment: doc.metadata.instructor.department || doc.metadata.institution.department || '',
      universityName: doc.metadata.institution.name,
      departmentName: doc.metadata.institution.department || doc.metadata.instructor.department || '',
      submissionDate: doc.metadata.submissionDate || '',
    });
    const coverDoc = template.compileToDocument(coverVals, doc.pageSettings);
    const coverSection = coverDoc.sections[0];

    if (!coverSection) return;

    const existingCoverIndex = doc.sections.findIndex((s) => s.type === 'cover');
    if (existingCoverIndex >= 0) {
      const existingSec = doc.sections[existingCoverIndex];
      updateSection(existingSec.id, {
        title: `${template.name} Cover`,
        blocks: coverSection.blocks,
      });
      showToast(`Updated Cover Page with ${template.name}.`, 'success');
    } else {
      const updatedDoc = {
        ...doc,
        sections: [{ ...coverSection, title: `${template.name} Cover` }, ...doc.sections],
      };
      loadDocument(updatedDoc);
      showToast(`Attached ${template.name} cover page to report.`, 'success');
    }
  };

  const handleUploadCustomCover = (file: File, fileDataUrl: string) => {
    const existingCoverIndex = doc.sections.findIndex((s) => s.type === 'cover');
    const coverBlocks: DocumentBlock[] = [
      {
        id: `blk-custom-cover-${Date.now()}`,
        type: 'image',
        createdAt: new Date().toISOString(),
        data: {
          assetId: `asset-${Date.now()}`,
          previewDataUrl: fileDataUrl,
          widthPercent: 95,
          alignment: 'center',
          caption: `Cover: ${file.name}`,
        },
      },
    ];

    if (existingCoverIndex >= 0) {
      const existingSec = doc.sections[existingCoverIndex];
      updateSection(existingSec.id, {
        title: `Cover (${file.name})`,
        blocks: coverBlocks,
      });
    } else {
      addSection(`Cover (${file.name})`, 'cover', 0);
    }
  };

  const handleProceedToReport = () => {
    const nonCover = doc.sections.find((s) => s.type !== 'cover');
    if (nonCover) {
      handleScrollToSection(nonCover.id);
    }
  };

  const handleDuplicateReport = () => {
    const clonedDoc = JSON.parse(JSON.stringify(doc));
    clonedDoc.metadata.id = `doc-cloned-${Date.now()}`;
    clonedDoc.metadata.title = `${doc.metadata.title} (Copy)`;
    clonedDoc.metadata.createdAt = new Date().toISOString();
    clonedDoc.metadata.updatedAt = new Date().toISOString();
    loadDocument(clonedDoc);
    showToast('Duplicated report as a new document.', 'success');
  };

  const handleScrollToSection = (sectionId: string) => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.outline = '2px solid var(--primary-500)';
      setTimeout(() => {
        el.style.outline = 'none';
      }, 1200);
    }
  };

  const handleToggleCollapseAll = (collapse: boolean) => {
    doc.sections.forEach((sec) => {
      updateSection(sec.id, { collapsed: collapse });
    });
  };

  const handleOpenOCR = (sectionId?: string) => {
    setOcrTargetSectionId(sectionId || (doc.sections[0]?.id ?? null));
    setIsOCRModalOpen(true);
  };

  const handleInsertOCRBlocks = (sectionId: string, blocks: DocumentBlock[]) => {
    const targetSection = doc.sections.find((s) => s.id === sectionId);
    if (!targetSection) {
      if (doc.sections.length > 0) {
        const fallback = doc.sections[0];
        updateSection(fallback.id, { blocks: [...fallback.blocks, ...blocks] });
      }
      return;
    }

    const updatedBlocks = [...targetSection.blocks, ...blocks];
    updateSection(sectionId, { blocks: updatedBlocks });
  };

  const getSaveStatusBadge = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <span className="badge badge-neutral" style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={11} className="animate-spin" /> Saving...
          </span>
        );
      case 'dirty':
        return (
          <span className="badge badge-neutral" style={{ fontSize: '11px', color: 'var(--color-warning)' }}>
            • Unsaved Changes
          </span>
        );
      case 'saved':
      default:
        return (
          <span className="badge badge-success" style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckCircle2 size={11} /> Saved (IndexedDB)
          </span>
        );
    }
  };

  const hasCoverSection = doc.sections.some((s) => s.type === 'cover');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Studio Header Toolbar */}
      <div
        style={{
          height: '52px',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface)',
          padding: '0 var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          gap: 'var(--space-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 'var(--radius-sm)',
              backgroundColor: 'var(--primary-50)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--primary-600)',
              flexShrink: 0,
            }}
          >
            <FlaskConical size={16} />
          </div>

          {/* Filename Input Control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <label
              htmlFor="report-filename-input"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
              }}
            >
              File name:
            </label>
            <input
              id="report-filename-input"
              type="text"
              className="form-input"
              value={doc.metadata.title}
              placeholder="e.g. Diode_Characteristics_Lab_Report"
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[\\/:*?"<>|]/g, '');
                updateMetadata({ title: sanitized });
              }}
              style={{
                maxWidth: '280px',
                minWidth: '140px',
                fontWeight: 600,
                height: '30px',
                padding: '2px 8px',
                fontSize: '12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
              }}
              title="Enter document and export file name (extension .pdf/.docx is handled automatically)"
            />
          </div>

          {getSaveStatusBadge()}
        </div>

        {/* History & Studio Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
          <button
            type="button"
            className="icon-btn"
            disabled={!canUndo}
            onClick={undo}
            title="Undo (Ctrl+Z)"
          >
            <Undo2 size={15} />
          </button>
          <button
            type="button"
            className="icon-btn"
            disabled={!canRedo}
            onClick={redo}
            title="Redo (Ctrl+Y)"
          >
            <Redo2 size={15} />
          </button>

          <span style={{ color: 'var(--border-default)', margin: '0 4px' }}>|</span>

          {/* Manual Edit Mode Toggle Button */}
          <button
            type="button"
            className={`btn ${isManualEditMode ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              backgroundColor: isManualEditMode ? 'var(--primary-600)' : undefined,
              color: isManualEditMode ? '#ffffff' : undefined,
            }}
            onClick={() => setIsManualEditMode(!isManualEditMode)}
            title="Toggle Word + Canva interactive Manual Edit Mode"
          >
            <Edit3 size={13} />
            <span>Manual Edit</span>
            {isManualEditMode && (
              <span
                style={{
                  fontSize: '9px',
                  backgroundColor: '#ffffff',
                  color: 'var(--primary-700)',
                  padding: '1px 5px',
                  borderRadius: '10px',
                  fontWeight: 700,
                }}
              >
                ON
              </span>
            )}
          </button>

          <span style={{ color: 'var(--border-default)', margin: '0 4px' }}>|</span>

          {/* OCR Scanner Button */}
          <Button
            variant="ghost"
            size="sm"
            icon={<Scan size={13} />}
            onClick={() => handleOpenOCR()}
            title="Recognize and extract formulas, tables, and notes from image"
          >
            OCR / Scan
          </Button>

          {/* New / Template */}
          <Button
            variant="ghost"
            size="sm"
            icon={<Plus size={13} />}
            onClick={() => setIsTemplateModalOpen(true)}
            title="Create new lab report or pick template"
          >
            New / Template
          </Button>

          {/* Report Metadata */}
          <Button
            variant="ghost"
            size="sm"
            icon={<Info size={13} />}
            onClick={() => setIsMetadataModalOpen(true)}
            title="Edit student name, course code, section, and lab info"
          >
            Report Info
          </Button>

          {/* Page Setup */}
          <Button
            variant="ghost"
            size="sm"
            icon={<Settings size={13} />}
            onClick={() => setIsPageSettingsModalOpen(true)}
            title="Configure paper size, margins, and typography"
          >
            Page Setup
          </Button>

          {/* Cover Page */}
          <Button
            variant="ghost"
            size="sm"
            icon={<Layers size={13} />}
            onClick={() => setIsCoverModalOpen(true)}
            title="Attach or update cover page"
          >
            {hasCoverSection ? 'Edit Cover' : 'Add Cover'}
          </Button>

          {/* Duplicate Document */}
          <button
            type="button"
            className="icon-btn"
            onClick={handleDuplicateReport}
            title="Duplicate Report"
          >
            <Copy size={15} />
          </button>

          <span style={{ color: 'var(--border-default)', margin: '0 4px' }}>|</span>

          {/* Save Button */}
          <Button
            variant="secondary"
            size="sm"
            icon={<Save size={13} />}
            onClick={manualSave}
          >
            Save
          </Button>

          {/* Print Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.print()}
            title="Print Report"
          >
            Print
          </Button>

          {/* Export Buttons */}
          <Button
            variant="secondary"
            size="sm"
            icon={<FileCode size={13} />}
            onClick={() => {
              setExportPreviewFormat('docx');
              setIsExportPreviewOpen(true);
            }}
          >
            DOCX
          </Button>

          <Button
            variant="primary"
            size="sm"
            icon={<Download size={13} />}
            onClick={() => {
              setExportPreviewFormat('pdf');
              setIsExportPreviewOpen(true);
            }}
          >
            Export PDF
          </Button>
        </div>
      </div>

      {/* Main 3-Column Studio Workspace */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Structure Outline Sidebar */}
        <ReportOutlineNav
          sections={doc.sections}
          onSelectSection={handleScrollToSection}
          onAddSection={(title, type) => addSection(title, type)}
          onMoveSection={(secId, dir) => moveSection(secId, dir)}
          onDuplicateSection={(secId) => duplicateSection(secId)}
          onToggleEnabled={(secId) => toggleSectionEnabled(secId)}
          onDeleteSection={(secId) => removeSection(secId)}
          onToggleCollapseAll={handleToggleCollapseAll}
          onOpenAddCoverModal={() => setIsCoverModalOpen(true)}
        />

        {/* Middle + Right Split Workspace */}
        <div className="split-workspace" style={{ flex: 1 }}>
          {/* Middle Modular Section Editor Panel */}
          {(viewMode === 'split' || viewMode === 'editor') && (
            <div className="split-left-panel" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-1) var(--space-2)' }}>
                <div>
                  <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', letterSpacing: '0.04em' }}>
                    Document Content Blocks
                  </span>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                    Build your laboratory report by adding mathematical formulas, tables, procedural notes, or OCR scans.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Scan size={13} />}
                    onClick={() => handleOpenOCR()}
                  >
                    Scan with OCR
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Plus size={13} />}
                    onClick={() => addSection('New Section', 'custom')}
                  >
                    Add Section
                  </Button>
                </div>
              </div>

              {/* Empty State if 0 sections */}
              {doc.sections.length === 0 ? (
                <div
                  style={{
                    padding: 'var(--space-8)',
                    textAlign: 'center',
                    border: '2px dashed var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-surface)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                  }}
                >
                  <FileSpreadsheet size={32} color="var(--primary-500)" />
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: 'var(--font-size-md)' }}>Your Lab Report is Empty</h3>
                    <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                      Start by choosing a specialized template or scan textbook / handwritten notes with OCR.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <Button variant="primary" size="sm" onClick={() => setIsTemplateModalOpen(true)}>
                      Choose Template
                    </Button>
                    <Button variant="secondary" size="sm" icon={<Scan size={13} />} onClick={() => handleOpenOCR()}>
                      Scan with OCR
                    </Button>
                  </div>
                </div>
              ) : (
                /* Render Active Section Item Editors */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  {doc.sections.map((sec, idx) => (
                    <SectionItemEditor
                      key={sec.id}
                      section={sec}
                      index={idx}
                      totalSections={doc.sections.length}
                      onOpenOCR={(secId) => handleOpenOCR(secId)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Right Live Document Preview Panel */}
          {(viewMode === 'split' || viewMode === 'preview') && (
            <div className="split-right-panel" style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
              {/* Contextual Manual Edit Toolbar */}
              {isManualEditMode && (
                <ManualEditToolbar
                  document={doc}
                  selectedBlockId={selectedBlockId}
                  selectedSectionId={selectedSectionId}
                  onSelectBlock={(bId, sId) => {
                    setSelectedBlockId(bId);
                    setSelectedSectionId(sId);
                  }}
                  onUpdateBlockData={(sId, bId, updates) => updateBlockData(sId, bId, updates)}
                  onRemoveBlock={(sId, bId) => {
                    removeBlock(sId, bId);
                    setSelectedBlockId(null);
                  }}
                  onDuplicateBlock={(sId, bId) => duplicateBlock(sId, bId)}
                  onReorderBlockLayer={(sId, bId, action) => reorderBlockLayer(sId, bId, action)}
                  onNudgeBlock={(sId, bId, dx, dy) => nudgeBlock(sId, bId, dx, dy)}
                  onInsertShape={(sId, shapeType) => insertShape(sId, shapeType)}
                  onInsertBlock={(sId, blockType) => addBlock(sId, blockType)}
                  onOpenFormulaModal={(formulaBlock, sId) => setEditingFormulaBlock({ block: formulaBlock, sectionId: sId })}
                  onOpenTableModal={(tableBlock, sId) => setEditingTableBlock({ block: tableBlock, sectionId: sId })}
                />
              )}

              {/* Floating Zoom & View Controls */}
              <div
                style={{
                  position: 'absolute',
                  top: isManualEditMode ? '54px' : '12px',
                  right: isManualEditMode ? '78px' : '16px',
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: 'var(--bg-surface)',
                  padding: '4px 8px',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-md)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 24, height: 24 }}
                  onClick={() => setZoomLevel((z) => Math.max(50, z - 15))}
                  title="Zoom Out"
                >
                  <ZoomOut size={13} />
                </button>
                <span style={{ fontSize: '11px', fontWeight: 600, minWidth: '36px', textAlign: 'center' }}>
                  {zoomLevel}%
                </span>
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 24, height: 24 }}
                  onClick={() => setZoomLevel((z) => Math.min(150, z + 15))}
                  title="Zoom In"
                >
                  <ZoomIn size={13} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 24, height: 24 }}
                  onClick={() => setZoomLevel(100)}
                  title="Reset 100% Zoom"
                >
                  <Maximize2 size={12} />
                </button>

                <span style={{ color: 'var(--border-default)', margin: '0 2px' }}>|</span>

                {/* View switcher for Mobile / Tablet */}
                <select
                  className="form-select text-xs"
                  style={{ width: '80px', height: '24px', padding: '1px 4px', fontSize: '10px' }}
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as any)}
                >
                  <option value="split">Split</option>
                  <option value="editor">Editor</option>
                  <option value="preview">Preview</option>
                </select>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0', transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center', transition: 'transform 0.15s ease-out' }}>
                <DocumentRenderer
                  document={doc}
                  manualEditOptions={{
                    isManualEditMode,
                    selectedBlockId,
                    selectedSectionId,
                    onSelectBlock: (blockId, sectionId) => {
                      setSelectedBlockId(blockId);
                      setSelectedSectionId(sectionId);
                    },
                    onUpdateText: (sectionId, blockId, text) => {
                      updateBlockData(sectionId, blockId, { text });
                    },
                    onNudge: (sectionId, blockId, dx, dy) => {
                      nudgeBlock(sectionId, blockId, dx, dy);
                    },
                    onDelete: (sectionId, blockId) => {
                      removeBlock(sectionId, blockId);
                      setSelectedBlockId(null);
                    },
                    onDuplicate: (sectionId, blockId) => {
                      duplicateBlock(sectionId, blockId);
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <LabTemplateSelectorModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      <ReportMetadataModal
        isOpen={isMetadataModalOpen}
        onClose={() => setIsMetadataModalOpen(false)}
        metadata={doc.metadata}
        onSave={(updates) => updateMetadata(updates)}
      />

      <PageSettingsModal
        isOpen={isPageSettingsModalOpen}
        onClose={() => setIsPageSettingsModalOpen(false)}
        pageSettings={doc.pageSettings}
        onSave={(updates) => updatePageSettings(updates)}
      />

      <AddCoverPageModal
        isOpen={isCoverModalOpen}
        onClose={() => setIsCoverModalOpen(false)}
        onApplyCoverTemplate={handleApplyCoverTemplate}
        onUploadCustomCover={handleUploadCustomCover}
        hasExistingCover={hasCoverSection}
        onProceedToReport={handleProceedToReport}
      />

      <OCRScannerModal
        isOpen={isOCRModalOpen}
        onClose={() => setIsOCRModalOpen(false)}
        sections={doc.sections}
        defaultSectionId={ocrTargetSectionId}
        onInsertBlocks={handleInsertOCRBlocks}
      />

      {/* Manual Edit Quick Edit Modals */}
      <FormulaEditModal
        isOpen={!!editingFormulaBlock}
        onClose={() => setEditingFormulaBlock(null)}
        formulaBlock={editingFormulaBlock?.block || null}
        sectionId={editingFormulaBlock?.sectionId || null}
        onSave={(sectionId, blockId, updates) => updateBlockData(sectionId, blockId, updates)}
      />

      <TableEditModal
        isOpen={!!editingTableBlock}
        onClose={() => setEditingTableBlock(null)}
        tableBlock={editingTableBlock?.block || null}
        sectionId={editingTableBlock?.sectionId || null}
        onSave={(sectionId, blockId, updates) => updateBlockData(sectionId, blockId, updates)}
      />

      {/* Export Preview Modal */}
      <ExportPreviewModal
        isOpen={isExportPreviewOpen}
        onClose={() => setIsExportPreviewOpen(false)}
        document={doc}
        defaultFormat={exportPreviewFormat}
        onExportPdf={handleExecutePdfExport}
        onExportDocx={handleExecuteDocxExport}
      />
    </div>
  );
};
